import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryParam } from '@/hooks/useQueryParam';
import { mergeQueryParams } from '@/utils/queryParams';

type UseTabsProps<T> = {
  defaultId?: T;
  onChange?: (id: T, index: number) => void;
  tabIds: T[];
};

const useTabs = <T extends string>(props: UseTabsProps<T>) => {
  const { defaultId, onChange, tabIds } = props;

  const defaultIndex = useMemo(() => {
    if (defaultId) {
      const tabIndex = tabIds.indexOf(defaultId);
      if (tabIndex !== -1) {
        return tabIndex;
      }
    }
    return 0;
  }, [defaultId, tabIds]);

  const [tabIndex, setTabIndex] = useState(defaultIndex);

  const setTabId = useCallback(
    (id: T) => {
      setTabIndex(tabIds.indexOf(id));
    },
    [tabIds],
  );

  useEffect(() => {
    if (onChange) {
      const id = tabIds[tabIndex];
      onChange(id, tabIndex);
    }
  }, [onChange, tabIds, tabIndex]);

  return {
    setTabId,
    setTabIndex,
    tabId: tabIds[tabIndex],
    tabIndex,
  };
};

type UseTabsQueryParamProps<T> = {
  defaultId?: T;
  queryKey?: string;
  tabIds: T[];
  onChange?: (tab: T) => void;
};

const useTabsQueryParam = <T extends string>(props: UseTabsQueryParamProps<T>) => {
  const { defaultId, queryKey = 'tab', tabIds } = props;
  const tabIdQueryParam = useQueryParam(queryKey) as T;

  const { setTabId, tabId } = useTabs<T>({
    defaultId: tabIds.includes(tabIdQueryParam) ? tabIdQueryParam : defaultId,
    tabIds,
  });

  const selectTab = (id: string) => {
    const newTab = tabIds.find((tab) => tab === id);
    if (!newTab) {
      throw Error('Tab id is not set!');
    }
    props.onChange?.(newTab);
    mergeQueryParams({
      set: [{ type: queryKey, value: newTab }],
    });
  };

  useEffect(() => {
    if (tabIds.includes(tabIdQueryParam) && tabIdQueryParam !== tabId) {
      setTabId(tabIdQueryParam as T);
    }
  }, [setTabId, tabId, tabIdQueryParam, tabIds]);

  return {
    selectTab,
    tabId,
  };
};

export default useTabsQueryParam;
